import React from "react";
import PersonIcon from "@mui/icons-material/Person";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { calculateAge } from "../utils/patientUtils";

interface ProfileHeaderProps {
  patientData: any;
  theme?: "light" | "dark";
  onEdit?: (id: string, e: React.MouseEvent) => void;
  onAddTreatment?: (id: string, e: React.MouseEvent) => void;
  onDelete?: (id: string, e: React.MouseEvent) => void;
  canDelete?: boolean;
  onClose: () => void;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  patientData,
  theme = "light",
  onEdit,
  onAddTreatment,
  onDelete,
  canDelete = false,
  onClose,
}) => {
  return (
    <div className={`rounded-lg border mb-4 sm:mb-6 ${
      theme === "dark" 
        ? "bg-slate-800 border-slate-700" 
        : "bg-white border-slate-200"
    }`}>
      <div className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4 flex-1 w-full">
            {patientData?.image ? (
              <img 
                src={patientData.image} 
                alt={patientData.fullName} 
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0" 
              />
            ) : (
              <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${
                theme === "dark" 
                  ? "bg-indigo-600" 
                  : "bg-indigo-100"
              }`}>
                <PersonIcon className={theme === "dark" ? "text-white" : "text-indigo-600"} sx={{ fontSize: { xs: 28, sm: 32 } }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                <h3 className={`text-base sm:text-lg font-semibold break-words ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                  {`${patientData?.title || ""} ${patientData?.fullName || "N/A"}`}
                </h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium self-start ${
                  theme === "dark"
                    ? "bg-indigo-900/50 text-indigo-200"
                    : "bg-indigo-100 text-indigo-700"
                }`}>
                  {patientData?.patientType || "Normal"}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                  <span className="font-medium">Reg No: </span>
                  <span className={`font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                    {patientData?.regNo || "N/A"}
                  </span>
                </div>
                <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                  <span className="font-medium">Gender: </span>
                  <span className={`font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                    {patientData?.gender || "N/A"}
                  </span>
                </div>
                {patientData?.dob && calculateAge(patientData.dob) !== null && (
                  <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                    <span className="font-medium">Age: </span>
                    <span className={`font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                      {calculateAge(patientData.dob)} years
                    </span>
                  </div>
                )}
                <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                  <span className="font-medium">Mobile: </span>
                  <span className={`font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                    {patientData?.mobileNo || "N/A"}
                  </span>
                </div>
                {patientData?.bloodGroup && (
                  <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                    <span className="font-medium">Blood Group: </span>
                    <span className={`font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                      {patientData.bloodGroup}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (patientData?._id && onEdit) {
                    onEdit(patientData._id, e);
                    onClose();
                  }
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === "dark"
                    ? "bg-amber-900/30 text-amber-300 hover:bg-amber-900/50"
                    : "bg-amber-50 text-amber-600 hover:bg-amber-100"
                }`}
                title="Edit"
              >
                <BorderColorIcon sx={{ fontSize: 16 }} />
              </button>
            )}
            {onAddTreatment && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (patientData?._id && onAddTreatment) {
                    onAddTreatment(patientData._id, e);
                    onClose();
                  }
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === "dark"
                    ? "bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50"
                    : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                }`}
                title="Add Treatment"
              >
                <MedicalInformationIcon sx={{ fontSize: 16 }} />
              </button>
            )}
            {onDelete && patientData?.hasid && canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (patientData?._id && onDelete) {
                    onDelete(patientData._id, e);
                    onClose();
                  }
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === "dark"
                    ? "bg-red-900/30 text-red-400 hover:bg-red-900/50"
                    : "bg-red-50 text-red-500 hover:bg-red-100"
                }`}
                title="Delete"
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;

